import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, typeof as _typeof, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useSafeThemeUI, isFlatDesign, utils, useRefresh, useForkRef, useSize, calculatedOffsets, useEffectOnce, PopoverAdorner, Button, Popover, scrollbar, getPopoverAndAnchorStatus } from '@costar/theme-ui';
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

var _excluded = ["variant", "popoverProps", "onOpen", "onClose", "open", "size", "children", "icon", "paneProps", "disabled", "fontSize", "minWidth", "emPaddingX", "automation-id"],
  _excluded2 = ["children", "paneProps"],
  _excluded3 = ["ref", "onMounted", "sx"];
// import { useWhatChanged } from '@simbathesailor/use-what-changed'

var isTop = function isTop(transformOrigin) {
  return (transformOrigin === null || transformOrigin === void 0 ? void 0 : transformOrigin.vertical) === 'bottom';
};
var borderShadowEclipseThickness = 6;
var defaultIconProps = {
  icon: 'mui-arrowDropDown',
  shouldFlipIcon: true
};
function isOverlapPopover(buttonElement, paneElement) {
  var _getPopoverAndAnchorS = getPopoverAndAnchorStatus(paneElement, buttonElement),
    isPopoverOverlappingAnchor = _getPopoverAndAnchorS.isPopoverOverlappingAnchor;
  return isPopoverOverlappingAnchor;
}
var PopoverSelectContainer = /*#__PURE__*/React.forwardRef(function PopoverSelectContainer(props, ref) {
  var theme = useSafeThemeUI(props);
  var isFlatDesign$1 = isFlatDesign(theme);
  var _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'theme-ui-select' : _props$variant,
    popoverProps = props.popoverProps,
    _props$onOpen = props.onOpen,
    onOpen = _props$onOpen === void 0 ? utils.noop : _props$onOpen,
    _props$onClose = props.onClose,
    onClose = _props$onClose === void 0 ? utils.noop : _props$onClose,
    openProp = props.open,
    size = props.size,
    children = props.children,
    _props$icon = props.icon,
    icon = _props$icon === void 0 ? 'mui-arrowDropDown' : _props$icon,
    _props$paneProps = props.paneProps,
    paneProps = _props$paneProps === void 0 ? utils.EMPTY_OBJECT : _props$paneProps,
    disabled = props.disabled,
    _fontSize = props.fontSize,
    _props$minWidth = props.minWidth,
    minWidth = _props$minWidth === void 0 ? '0' : _props$minWidth,
    _props$emPaddingX = props.emPaddingX,
    emPaddingX = _props$emPaddingX === void 0 ? 'xs' : _props$emPaddingX,
    automationId = props['automation-id'],
    rest = _objectWithoutProperties(props, _excluded);
  var fontSize = _fontSize ? _fontSize : size;
  var _ref = popoverProps || {
      children: /*#__PURE__*/React.createElement("div", null)
    },
    popoverContent = _ref.children,
    popoverPaneProps = _ref.paneProps,
    popoverRest = _objectWithoutProperties(_ref, _excluded2);
  var _popoverPaneProps$pan = _objectSpread2(_objectSpread2({}, popoverPaneProps), paneProps),
    panePropsRef = _popoverPaneProps$pan.ref,
    _popoverPaneProps$pan2 = _popoverPaneProps$pan.onMounted,
    onMounted = _popoverPaneProps$pan2 === void 0 ? utils.noop : _popoverPaneProps$pan2,
    _popoverPaneProps$pan3 = _popoverPaneProps$pan.sx,
    sxPaneProp = _popoverPaneProps$pan3 === void 0 ? utils.EMPTY_OBJECT : _popoverPaneProps$pan3,
    restPaneProps = _objectWithoutProperties(_popoverPaneProps$pan, _excluded3);
  var iconProps = typeof icon === 'string' && icon !== 'none' ? _objectSpread2(_objectSpread2({}, defaultIconProps), {}, {
    icon: icon
  }) : _typeof(icon) === 'object' && utils.isNotEmptyObject(icon) ? _objectSpread2(_objectSpread2({}, defaultIconProps), icon) : utils.EMPTY_OBJECT;
  var refreshCallback = useRefresh();
  var identifier = utils.useUniqueId('popover-select-container');
  var anchorElRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    anchorEl = _useState2[0],
    setAnchorEl = _useState2[1];
  var buttonRef = useRef(null);
  var paneRef = useRef(null);
  var handelPaneRef = useForkRef(paneRef, panePropsRef);
  var handelRef = useForkRef(ref, buttonRef);
  var mountRef = useRef(false);
  var buttonSize = useSize(buttonRef);

  // refresh render when button size change, to adjust the adorner for the popover.
  useEffect(function () {
    return refreshCallback();
  }, [buttonSize, refreshCallback]);

  // useWhatChanged([setAnchorEl, onClose], 'setAnchorEl, onClose')

  var setClosePopover = useCallback(function () {
    if (anchorElRef.current) {
      // eslint-disable-next-line no-console
      // console.log(` ----- popover-select-container setClosePopover() -----`)
      anchorElRef.current = null;
      setAnchorEl(null);
      onClose && onClose();
      setTimeout(function () {
        buttonRef.current && buttonRef.current.focus();
      }, 1);
    }
  }, [onClose]);

  // useWhatChanged([setClosePopover, openProp], 'setClosePopover, openProp')
  useEffect(function () {
    if (mountRef.current && !openProp) setClosePopover();
  }, [setClosePopover, openProp]);
  var handleClick = useCallback(function (event) {
    event.stopPropagation();
    // eslint-disable-next-line no-console
    // console.log(`handleClick ${identifier}`)
    // need use closure to save the target instance to avoid synthetic event error.
    var currentTarget = event.currentTarget;
    setAnchorEl(function (state) {
      var retVal = !state ? currentTarget : null;
      if (retVal) {
        onOpen && onOpen();
      } else {
        onClose && onClose();
      }
      anchorElRef.current = retVal;
      return retVal;
    });
  }, [onClose, onOpen]);
  var handleClose = useCallback(function () {
    // eslint-disable-next-line no-console
    // console.log(`handleClose ${identifier}`)
    setClosePopover();
  }, [setClosePopover]);
  var handleOnEntered = useCallback(function (paneElement, _isAppearing) {
    handelPaneRef(paneElement);
    refreshCallback();
  }, [handelPaneRef, refreshCallback]);
  var handleOnPaneMounted = useCallback(function () {
    return setTimeout(utils.callAll(onMounted, refreshCallback), 200);
  }, [onMounted, refreshCallback]);
  var panePropsCss = useMemo(function () {
    if (size) {
      var offset = utils.getStepSize(size, 2);
      return {
        __css: {
          maxWidth: calculatedOffsets(offset, theme),
          maxHeight: calculatedOffsets(offset, theme)
        }
      };
    }
    return {};
  }, [size, theme]);
  useEffectOnce(function () {
    mountRef.current = true;
  });
  var open = Boolean(anchorEl);
  var id = open ? identifier : undefined;
  var renderAdornerComponent = useCallback(function () {
    return !isOverlapPopover(buttonRef.current, paneRef.current) ? /*#__PURE__*/React.createElement(PopoverAdorner, {
      variant: variant,
      anchorRect: buttonRef.current ? buttonRef.current.getBoundingClientRect() : null,
      dropdownRect: paneRef.current ? paneRef.current.getBoundingClientRect() : null,
      isTop: isTop(popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.transformOrigin),
      borderShadowEclipseThickness: borderShadowEclipseThickness
    }) : null;
  }, [popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.transformOrigin, variant]);
  // eslint-disable-next-line no-console
  // console.log(`render popover-select-container open: ${open}`)
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, _extends({
    "automation-id": automationId,
    ref: handelRef,
    variant: variant,
    "aria-describedby": id,
    onClick: disabled ? undefined : handleClick,
    toggled: open,
    disabled: disabled,
    size: size,
    fontSize: fontSize,
    "aria-expanded": !disabled && open,
    minWidth: minWidth
  }, _objectSpread2(_objectSpread2({}, utils.isNotEmptyObject(iconProps) ? {
    emPaddingX: emPaddingX,
    iconAfter: _objectSpread2(_objectSpread2({}, iconProps), {}, {
      toggleState: open
    })
  } : {
    p: 0
  }), rest)), children), /*#__PURE__*/React.createElement(Popover, _extends({
    id: id,
    open: !disabled && open,
    anchorEl: anchorEl,
    onClose: handleClose,
    onEntered: handleOnEntered,
    hideBackdrop: true,
    disableAutoFocus: true,
    disableEnforceFocus: true,
    disableRestoreFocus: true,
    disableScrollLock: true,
    adornerComponent: isFlatDesign$1 ? undefined : renderAdornerComponent
  }, _objectSpread2({
    elevation: 'none',
    zIndex: isOverlapPopover(buttonRef.current, paneRef.current) ? 'selectOverlapPopover' : 'selectPopover',
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    paneProps: _objectSpread2(_objectSpread2({
      elevation: 'none',
      bg: 'white',
      borderRadius: isFlatDesign$1 ? 'default' : 'none',
      border: isFlatDesign$1 ? 'focus' : 'primary',
      boxShadow: isFlatDesign$1 ? 'dropShadow' : 'primary',
      onMounted: handleOnPaneMounted,
      fontSize: fontSize,
      sx: _objectSpread2(_objectSpread2({}, scrollbar.transition), sxPaneProp)
    }, panePropsCss), restPaneProps),
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    clickAwayListenerProps: {
      ignoreElement: anchorEl,
      mouseEvent: 'onMouseDown',
      onClickAway: handleClose
    }
  }, popoverRest)), typeof popoverContent === 'function' ? popoverContent() : popoverContent));
});

export default PopoverSelectContainer;
//# sourceMappingURL=popover-select-container.esm.js.map
